'use client';

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConnectorStore } from '@/lib/connectors/store';
import type { ConnectorOperation } from '@/lib/connectors/types';
import { Code, FormInput } from 'lucide-react';

interface RequestBodyEditorProps {
  connectorId: string;
  operation: ConnectorOperation;
}

const CONTENT_TYPES = [
  { value: 'application/json', label: 'JSON' },
  { value: 'application/x-www-form-urlencoded', label: 'Form URL Encoded' },
  { value: 'multipart/form-data', label: 'Multipart Form Data' },
];

export function RequestBodyEditor({ connectorId, operation }: RequestBodyEditorProps) {
  const { updateOperation } = useConnectorStore();
  const [editMode, setEditMode] = useState<'visual' | 'raw'>('raw');

  const contentType = operation.request_content_type || 'application/json';
  const schema = operation.request_body_schema || {};

  const handleContentTypeChange = async (value: string) => {
    try {
      await updateOperation(connectorId, operation.id, { request_content_type: value });
    } catch (error) {
      console.error('Failed to update content type:', error);
    }
  };

  const handleSchemaChange = async (schemaStr: string) => {
    try {
      const parsed = JSON.parse(schemaStr);
      await updateOperation(connectorId, operation.id, { request_body_schema: parsed });
    } catch {
      // Invalid JSON, don't update
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Request Body</CardTitle>
            <CardDescription>
              Define the structure of the request body
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <Select value={contentType} onValueChange={handleContentTypeChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map((ct) => (
                  <SelectItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>JSON Schema</Label>
            <Textarea
              value={JSON.stringify(schema, null, 2)}
              onChange={(e) => handleSchemaChange(e.target.value)}
              placeholder={'{\n  "type": "object",\n  "properties": {\n    "name": { "type": "string" }\n  }\n}'}
              className="font-mono text-sm min-h-[300px]"
            />
            <p className="text-sm text-muted-foreground">
              Enter a valid JSON Schema for the request body
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
